import { defineComponent as _defineComponent } from 'vue'
import { appLink } from '@@/bits/app_link'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import type { ResizeObserver } from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { observeSizeChange } from '@@/bits/resize_observer'
import { checkIfUserIsInZoomCollaborateMode } from '@@/bits/zoom'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import OzIconButton, {
  OzIconButtonColorScheme,
  OzIconButtonSizePreset,
} from '@@/library/v4/components/OzIconButton.vue'
import OzPadletLogo, { OzPadletLogoColorScheme } from '@@/library/v4/components/OzPadletLogo.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfaceSettingsStore } from '@@/pinia/surface_settings'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import AppBarAccountAvatar from '@@/vuecomponents/AppBarAccountAvatar.vue'
import SurfaceGuestIdLabel from '@@/vuecomponents/SurfaceGuestIdLabel.vue'
import SurfaceTitleMobile from '@@/vuecomponents/SurfaceTitleMobile.vue'
import { useHandleFirstVerticalSwipe } from '@@/vuecomposables/useHandleFirstVerticalSwipe'
import { GroupByTypes } from '@padlet/arvo'
import { debounce } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'SurfaceHeaderMobile',
  setup(__props, { expose }) {

const rootEl = ref<HTMLDivElement>()
const surfaceTitle = ref<InstanceType<typeof SurfaceTitleMobile>>()
const surfaceNavbar = ref<HTMLDivElement>()

const surfaceStore = useSurfaceStore()
const {
  isEmbedded,
  isElectronApp,
  xHeader,
  user,
  isUserHostingZoomCollaboration,
  headerHeight,
  dir,
  headerColor,
  backgroundLuminance,
  colorScheme,
  isMap,
  homeUrl,
  isSectionBreakout,
  isReadOnly,
  wallAttributes,
  isFrozen,
  isGrid,
  canUseSections,
  skinPrimaryVariantColorAsRGB,
  isSkinColorAvailable,
} = storeToRefs(surfaceStore)
const { resizeHeader, checkAndSubscribeToPushNotifications } = surfaceStore

const { wishGroupBy } = storeToRefs(useSurfaceWishArrangementStore())

const surfacePermissionsStore = useSurfacePermissionsStore()
const { amIRegistered, canIAdminister } = storeToRefs(surfacePermissionsStore)

const surfaceSettingsStore = useSurfaceSettingsStore()
const { toggleSettingsPanel } = surfaceSettingsStore

const surfaceGuestStore = useSurfaceGuestStore()

const resizeObserver = ref<ResizeObserver | null>(null)

// COLLAPSE
const isCollapsed = ref<boolean | null>(null)
function collapse(): void {
  isCollapsed.value = true
  updateHeaderHeight()
}
function toggleIsCollapsed() {
  isCollapsed.value = !isCollapsed.value
  updateHeaderHeight()
}

function updateHeaderHeight(): void {
  // Since the resize is debounced, we need to check
  // that these elements still exist.
  if (!surfaceNavbar.value || !surfaceTitle.value) return
  if (!isCollapsed.value) {
    nextTick(() => {
      if (!surfaceNavbar.value || !surfaceTitle.value) return
      resizeHeader({
        height: surfaceNavbar.value.getBoundingClientRect().height + surfaceTitle.value.$el.clientHeight ?? 0,
      })
    })
  } else {
    resizeHeader({ height: 48 })
  }
}

// Obfuscate the app link
function openAppLink() {
  navigateTo(appLink)
}

// Collapse header on first vertical swipe for touch devices (only grid format w sections)
const isGridWithSections = computed(
  () => isGrid.value && canUseSections.value && wishGroupBy.value !== GroupByTypes.None,
)
if (isGridWithSections.value) {
  useHandleFirstVerticalSwipe(collapse)
}

// ZOOM
const isUserInZoomCollaborationMode = ref(false)
const isZoomCollaborationGuest = computed(
  () => isUserInZoomCollaborationMode.value === true && isUserHostingZoomCollaboration.value === false,
)
// END ZOOM

const hasDarkBackgroundLuminance = computed(() => {
  if (isMap.value) {
    return colorScheme.value !== 'light'
  }

  return backgroundLuminance.value !== 'light'
})

const realBackgroundLuminance = computed<'light' | 'dark'>(() => {
  if (isMap.value) {
    return colorScheme.value
  }
  return backgroundLuminance.value
})

onMounted(async () => {
  // Don't prompt for notifications if the user is not registered and the app is not the electron app
  if (amIRegistered.value && !isElectronApp.value) checkAndSubscribeToPushNotifications()
  updateHeaderHeight()
  if (rootEl.value != null) {
    resizeObserver.value = observeSizeChange(rootEl.value, debounce(updateHeaderHeight, 250))
  }
  isUserInZoomCollaborationMode.value = await checkIfUserIsInZoomCollaborateMode()
})

onBeforeUnmount(() => {
  resizeObserver.value?.disconnect()
})

expose({
  collapse,
})

return { __sfc: true,rootEl, surfaceTitle, surfaceNavbar, surfaceStore, isEmbedded, isElectronApp, xHeader, user, isUserHostingZoomCollaboration, headerHeight, dir, headerColor, backgroundLuminance, colorScheme, isMap, homeUrl, isSectionBreakout, isReadOnly, wallAttributes, isFrozen, isGrid, canUseSections, skinPrimaryVariantColorAsRGB, isSkinColorAvailable, resizeHeader, checkAndSubscribeToPushNotifications, wishGroupBy, surfacePermissionsStore, amIRegistered, canIAdminister, surfaceSettingsStore, toggleSettingsPanel, surfaceGuestStore, resizeObserver, isCollapsed, collapse, toggleIsCollapsed, updateHeaderHeight, openAppLink, isGridWithSections, isUserInZoomCollaborationMode, isZoomCollaborationGuest, hasDarkBackgroundLuminance, realBackgroundLuminance, device, isAppUsing, __, OzContainedButton, OzContainedButtonColorScheme, OzContainedButtonSizePreset, OzIconButton, OzIconButtonColorScheme, OzIconButtonSizePreset, OzPadletLogo, OzPadletLogoColorScheme, AppBarAccountAvatar, SurfaceGuestIdLabel, SurfaceTitleMobile }
}

})